import { useEffect, useState } from 'react';
import { facebookLogin } from '../utils/facebookSdk.js';
import { googleLogin, loadGoogleIdentity } from '../utils/googleIdentity.js';
import { oauthLogin } from '../utils/oauthLogin.js';

export const GOOGLE_AUTH_COMING_SOON = false;
export const FACEBOOK_AUTH_COMING_SOON = false;

const comingSoon = {
  google: GOOGLE_AUTH_COMING_SOON,
  facebook: FACEBOOK_AUTH_COMING_SOON
};

const buttonClass =
  'flex h-10 w-full items-center justify-center gap-2.5 rounded-md bg-white px-3 text-sm font-medium text-gray-700 outline outline-1 -outline-offset-1 outline-gray-300 hover:bg-gray-50 disabled:opacity-60';

function GoogleMark() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.208 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.188 0-9.62-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

function FacebookMark() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#1877F2"
        d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.49 0-1.953.93-1.953 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073"
      />
    </svg>
  );
}

export default function SocialAuthButtons({ active, onSuccess, disabled }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!active || GOOGLE_AUTH_COMING_SOON) return undefined;
    loadGoogleIdentity().catch(() => {});
    return undefined;
  }, [active]);

  const handleSocial = async (provider) => {
    if (comingSoon[provider] || disabled || busy) return;
    setError('');
    setBusy(provider);
    try {
      const token = provider === 'google' ? await googleLogin() : await facebookLogin();
      if (!token) {
        return;
      }
      const data = await oauthLogin(provider, token);
      onSuccess(data);
    } catch (err) {
      setError(err.message || 'Sign-in failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => handleSocial('google')}
        disabled={GOOGLE_AUTH_COMING_SOON || disabled || Boolean(busy)}
        className={buttonClass}
      >
        <GoogleMark />
        {busy === 'google' ? 'Connecting…' : 'Continue with Google'}
      </button>
      {FACEBOOK_AUTH_COMING_SOON && (
        <p className="text-center text-xs font-medium text-slate-600">
          Coming soon
        </p>
      )}
      <button
        type="button"
        onClick={() => handleSocial('facebook')}
        disabled={FACEBOOK_AUTH_COMING_SOON || disabled || Boolean(busy)}
        className={buttonClass}
      >
        <FacebookMark />
        {busy === 'facebook' ? 'Connecting…' : 'Continue with Facebook'}
      </button>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="relative py-1">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-gray-50 px-2 text-xs text-gray-500">or continue with email</span>
        </div>
      </div>
    </div>
  );
}
